import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  getKioskSession,
  getKioskData,
  type KioskSession,
  type KioskDataResponse,
  type KioskAnnouncement,
  type KioskWeather,
  type KioskInspiration,
} from '../services/quickAccess';
import { getDefaultLocation } from '../services/weather';

// ============================================
// TYPES
// ============================================

interface KioskContextValue {
  // Session info
  isKioskMode: boolean;
  session: KioskSession | null;

  // Data
  announcements: KioskAnnouncement[];
  weather: KioskWeather | null;
  inspiration: KioskInspiration | null;

  // Loading states
  loading: boolean;
  error: string | null;

  // Actions
  refresh: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const KioskContext = createContext<KioskContextValue | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface KioskProviderProps {
  children: ReactNode;
}

export const KioskProvider: React.FC<KioskProviderProps> = ({ children }) => {
  const [session, setSession] = useState<KioskSession | null>(null);
  const [data, setData] = useState<KioskDataResponse>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for kiosk session on mount
  useEffect(() => {
    const kioskSession = getKioskSession();
    setSession(kioskSession);

    if (!kioskSession) {
      setLoading(false);
    }
  }, []);

  // Fetch kiosk data when session is available
  const fetchData = useCallback(async () => {
    if (!session) return;

    setLoading(true);
    setError(null);

    try {
      const location = getDefaultLocation();
      const result = await getKioskData(session.householdId, {
        include: {
          announcements: true,
          weather: true,
          inspiration: true,
        },
        weatherOptions: {
          lat: location.lat,
          lon: location.lon,
          units: 'imperial',
        },
      });

      if (result.error) {
        setError(result.error);
      } else {
        setData(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch kiosk data');
    } finally {
      setLoading(false);
    }
  }, [session]);

  // Initial fetch and periodic refresh
  useEffect(() => {
    if (!session) return;

    fetchData();

    // Refresh data every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [session, fetchData]);

  const value: KioskContextValue = {
    isKioskMode: session !== null,
    session,
    announcements: data.announcements || [],
    weather: data.weather || null,
    inspiration: data.inspiration || null,
    loading,
    error,
    refresh: fetchData,
  };

  return (
    <KioskContext.Provider value={value}>
      {children}
    </KioskContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export function useKiosk(): KioskContextValue {
  const context = useContext(KioskContext);
  if (context === undefined) {
    // Return a default "not in kiosk mode" value instead of throwing
    // This allows components to check isKioskMode safely
    return {
      isKioskMode: false,
      session: null,
      announcements: [],
      weather: null,
      inspiration: null,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }
  return context;
}

// ============================================
// UTILITY HOOK FOR KIOSK-AWARE WIDGETS
// ============================================

/**
 * Hook for widgets that need to work in both authenticated and kiosk modes.
 * Returns kiosk announcements if in kiosk mode, otherwise returns null
 * so the widget can fall back to its normal data fetching.
 */
export function useKioskAnnouncements() {
  const { isKioskMode, announcements, loading } = useKiosk();

  if (!isKioskMode) {
    return { isKioskMode: false, announcements: null, loading: false };
  }

  return { isKioskMode: true, announcements, loading };
}

/**
 * Hook for weather widget in kiosk mode
 */
export function useKioskWeather() {
  const { isKioskMode, weather, loading } = useKiosk();

  if (!isKioskMode) {
    return { isKioskMode: false, weather: null, loading: false };
  }

  return { isKioskMode: true, weather, loading };
}

/**
 * Hook for inspiration widget in kiosk mode
 */
export function useKioskInspiration() {
  const { isKioskMode, inspiration, loading } = useKiosk();

  if (!isKioskMode) {
    return { isKioskMode: false, inspiration: null, loading: false };
  }

  return { isKioskMode: true, inspiration, loading };
}
